import { useEffect, useState, useMemo } from 'react';
import { useTransition, config } from 'react-spring';
import optimizeImage from '../../../../shared/utils/imageOptimizer';

/**
 * Hook to manage background slides with transitions
 * @param {Array} slidesData - Array of slide objects with { id, url } or array of image URLs
 * @param {Object} options - Configuration options
 * @param {number} options.interval - Interval in milliseconds between slides (default: 6000)
 * @param {boolean} options.autoPlay - Whether to auto-play slides (default: true)
 * @returns {Object} - { imageTransition, imageIndex, setImageIndex }
 */
const useBackgroundSlides = (slidesData = [], options = {}) => {
  const { interval = 6000, autoPlay = true } = options;
  const [imageIndex, setImageIndex] = useState(0);

  // Normalize slides data - handle both array of URLs and array of objects
  const slides = useMemo(() => slidesData.map((slide, index) => {
    if (typeof slide === 'string') {
      return {
        id: index,
        url: slide,
      };
    }
    return {
      id: slide.id !== undefined ? slide.id : index,
      url: slide.url,
    };
  }).filter((slide) => slide.url), [slidesData]); // Filter out slides without URLs

  // Ensure imageIndex is within bounds and memoize currentSlide
  const safeImageIndex = slides.length > 0 ? imageIndex % slides.length : 0;
  const currentSlide = useMemo(() => (
    slides.length > 0 ? slides[safeImageIndex] : null
  ), [slides, safeImageIndex]);

  const imageTransition = useTransition(
    currentSlide,
    (item) => (item ? item.id : 'no-slide'),
    {
      from: { opacity: 0 },
      enter: { opacity: 1 },
      leave: { opacity: 0 },
      config: config.molasses,
    },
  );

  useEffect(() => {
    if (!autoPlay || slides.length <= 1) {
      return () => {};
    }

    const intervalId = setInterval(() => {
      setImageIndex((state) => (state + 1) % slides.length);
    }, interval);

    return () => clearInterval(intervalId);
  }, [slides.length, interval, autoPlay]);

  return {
    imageTransition,
    imageIndex,
    setImageIndex,
  };
};

/**
 * Creates a slides array from login background images
 * @param {number} count - Number of slides to create (default: 12)
 * @returns {Array} - Array of slide objects with { id, url }
 */
export const createLoginSlides = (count = 12) => {
  const slides = Array(count).fill(0);

  slides.forEach((slide, index, slidesArray) => {
    const slidesArrayObject = slidesArray;

    slidesArrayObject[index] = {
      id: index,
      url: optimizeImage(
        `https://s3.amazonaws.com/teambuildr-${process.env.ENVIRONMENT !== 'PRODUCTION' ? 'staging' : 'production'}-assets/backgrounds/image_login_${index + 1}.jpg`,
        {
          resize: {
            width: 1500, height: 1500, fit: 'inside',
          },
        },
      ),
    };
  });

  return slides;
};

export default useBackgroundSlides;
